import axios from "axios";
import { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Table,
    Card,
    Input,
    Row,
    Col,
    Select,
    Modal,
    Form,
    InputNumber,
    Button,
    DatePicker,
    Upload,
    message,
} from "antd";
import {
    PlusOutlined,
    UploadOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { toWords } from "number-to-words";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import SalaryCalculator from "../../components/SalaryCalculator";
import SalaryCalculation from "./SalaryCalculation";
dayjs.extend(customParseFormat);

const numberToWords = (num) => {
    if (!num || num === 0) return "Zero";
    return (
        toWords(Math.round(num)).replace(/\b\w/g, (l) => l.toUpperCase()) + " Only"
    );
};

/* ------------------- NEW calculateSalary – EXACTLY like HrOneCandidate ------------------- */
const calculateSalary = (values) => {
    const {
        basic_earned = 0,
        hra_earned = 0,
        conveyance_earned = 0,
        other_allowance_earned = 0,
        pf_employee = 0,
        esi_employee = 0,
    } = values;

    const gross_earned =
        Number(basic_earned) +
        Number(hra_earned) +
        Number(conveyance_earned) +
        Number(other_allowance_earned);

    const total_deductions = Number(pf_employee) + Number(esi_employee);
    const net_pay = gross_earned - total_deductions;
    const net_pay_in_words = numberToWords(net_pay);

    // ESI eligibility rule: No if gross >= 23447, else Yes
    const is_esi = gross_earned >= 23447 ? "No" : "Yes";

    return {
        gross_earned,
        total_deductions,
        net_pay,
        net_pay_in_words,
        is_esi, // optional: can be used if you display it
    };
};

export default function ActualSalaryStatement() {
    /* ------------------- State ------------------- */
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [departmentNames, setDepartmentNames] = useState([]);
    const [designationNames, setDesignationNames] = useState([]);
    const [organizationNames, setOrganizationNames] = useState([]);
    const [form] = Form.useForm();
    const [dirtyFields, setDirtyFields] = useState(new Set());
    const [selectedSalary, setSelectedSalary] = useState({
        id: 0,
        basic: 0,
        hra: 0,
        conveyance: 0,
        others: 0,
        pf: 0,
        esi: 0,
        gross: 0,
        total_deduction: 0,
        net: 0
    });

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
    };

    const { backUrl, authData } = useContext(AuthContext);

    const [filters, setFilters] = useState({
        name: "",
        emp_code: "",
        month: "",
        year: "",
        status: "",
        organization: "",
        department: '',
        designation: ''
    });

    // Upload modal
    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
    const [uploadForm] = Form.useForm();
    const [uploading, setUploading] = useState(false);

    // Generate Salary Modal
    const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false);
    const [generateForm] = Form.useForm();
    const [generating, setGenerating] = useState(false);

    /* ------------------- API ------------------- */
    const fetchData = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/payroll/drafts/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });
            const results = res.data.results || [];
            setData(results);
            setFilteredData(results);
        } catch (error) {
            console.error("Error fetching salary statement:", error);
            toast.error("Failed to fetch data");
        }
    };

    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganizationNames(res.data.results);
        } catch (err) {
            console.error("Failed to fetch organizations:", err);
        }
    };

    const fetchDepartments = async (v) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${v}`);
            setDepartmentNames(res.data.results);
        } catch (err) {
            console.error("Failed to fetch departments:", err);
        }
    };

    const fetchDesignations = async (departmentId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${departmentId}`);
            setDesignationNames(res.data.results);
        } catch (err) {
            console.error("Error fetching designations", err);
        }
    };

    /* ------------------- Generate Salary Modal Handlers ------------------- */
    const handleOpenGenerateModal = () => {
        generateForm.resetFields();
        setIsGenerateModalVisible(true);
    };

    const handleCloseGenerateModal = () => {
        setIsGenerateModalVisible(false);
        generateForm.resetFields();
    };

    const handleGenerateSalary = async () => {
        try {
            const values = await generateForm.validateFields();
            const { month_year } = values;

            const month = dayjs(month_year).month() + 1;
            const year = dayjs(month_year).year();

            setGenerating(true);
            message.loading({ content: "Generating salary...", key: "generateSalary" });

            const res = await axios.post(
                `${backUrl}/api/payroll/generate-all-drafts/?month=${month}&year=${year}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );

            message.success({ content: res.data.message, key: "generateSalary" });
            toast.success(res.data.message);
            handleCloseGenerateModal();
            fetchData();
        } catch (error) {
            console.error("Error generating salary:", error);
            message.error({ content: "Failed to generate salary", key: "generateSalary" });
            toast.error(error.response?.data?.detail || "Failed to generate salary");
        } finally {
            setGenerating(false);
        }
    };

    /* ------------------- Upload ------------------- */
    const handleOpenUploadModal = () => setIsUploadModalVisible(true);
    const handleCloseUploadModal = () => {
        setIsUploadModalVisible(false);
        uploadForm.resetFields();
    };

    const handleUploadSalary = async () => {
        try {
            const values = await uploadForm.validateFields();
            const { month_year, file } = values;

            const formData = new FormData();
            const month = dayjs(month_year).month() + 1;
            const year = dayjs(month_year).year();

            formData.append("month", month.toString());
            formData.append("year", year.toString());
            formData.append("file", file[0].originFileObj);

            setUploading(true);
            const res = await axios.post(
                `${backUrl}/api/payroll/upload-salary-excel/`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            toast.success(res.data.message || "Salary details uploaded successfully!");
            handleCloseUploadModal();
            fetchData();
        } catch (error) {
            console.error("Upload error:", error);
            toast.error(
                error.response?.data?.detail || "Failed to upload salary details!"
            );
        } finally {
            setUploading(false);
        }
    };

    /* ------------------- Download ------------------- */
    const handleDownloadExcel = () => {
        if (!filteredData.length) {
            toast.warning("No records to download!");
            return;
        }

        const excelData = filteredData.map((item) => ({
            "Emp Code": item.employee?.emp_code || "",
            "Name": `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`,
            "Organisation": item.employee?.organisation_name || "",
            "Department": item.employee?.department_name || "",
            "Designation": item.employee?.designation_name || "",

            "Month Days": item.month_days || "",
            "Days Payable": item.days_payable || "",
            "Basic": item.basic_earned || "",
            "Gross": item.gross_earned || "",
            "HRA": item.hra_earned || "",
            "Conveyance": item.conveyance_earned || "",
            "Other Allowances": item.other_allowance_earned || "",
            "OT Days": item.no_of_ot_days || "",
            "OT Hours": item.no_of_ot_hours || "",
            "OT Pay": item.ot_pay || "",
            "PT": item.pt_pm || "",
            "Employer PF (12%)": item.pf_employer_pm || "",
            "Employer ESI (3.25%)": item.esi_employer_pm || "",
            "Employee PF": item.pf_employee_pm || "",
            "Employee ESI": item.esi_employee_pm || "",
            "Flexible Benefit Allowance": item.flexible_benefit_allowance || "",
            "Late days": item.late_days || "",
            "Late deduction": item.late_deduction || "",
            "LOP days": item.lop_days || "",
            "LOP deduction": item.lop_deduction || "",
            "Salary Advance": item.salary_advance || "",
            "Arrears": item.arrears || "",
            "Total CTC": item.ctc_earned || "",
            "Net Pay": item.net_pay_earned || "",
            "Net Pay in Words": item.net_pay_in_words || "",
            "Salary type": item.salary_type || "",
            "Salary status": item.salary_status || "",

            "CL OP": item.cl_opening || "",
            "SL OP": item.sl_opening || "",
            "EL OP": item.el_opening || "",
            "CL Availed": item.cl_availed || "",
            "SL Availed": item.sl_availed || "",
            "EL Availed": item.el_availed || "",
            "CL Balance": item.cl_balance || "",
            "SL Balance": item.sl_balance || "",
            "EL Balance": item.el_balance || "",

            "Date": item.date || "",
            "Month": item.salary_month || "",
            "Year": item.year || "",
            "Approval Status": item.approve_status || "",
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Salary Statement");

        XLSX.writeFile(wb, "Actual_Salary_Statement.xlsx");
        toast.success("Excel downloaded successfully!");
    };

    /* ------------------- Filtering ------------------- */
    useEffect(() => {
        fetchData();
        fetchOrganizations()
    }, []);

    useEffect(() => {
        let filtered = [...data];

        if (filters.organization)
            filtered = filtered.filter(
                (i) => i.employee?.organisation_name === filters.organization
            );

        if (filters.department)
            filtered = filtered.filter(
                (i) => i.employee?.department_name === filters.department
            );

        if (filters.designation)
            filtered = filtered.filter(
                (i) => i.employee?.designation_name === filters.designation
            );

        if (filters.emp_code)
            filtered = filtered.filter((i) =>
                i.employee?.emp_code
                    ?.toLowerCase()
                    .includes(filters.emp_code.toLowerCase())
            );

        if (filters.name)
            filtered = filtered.filter((i) => {
                const full = `${i.employee?.first_name || ""} ${i.employee?.last_name || ""}`.toLowerCase();
                return full.includes(filters.name.toLowerCase());
            });

        if (filters.month)
            filtered = filtered.filter((i) => String(i.month) === String(filters.month));

        if (filters.year)
            filtered = filtered.filter((i) => String(i.year) === String(filters.year));

        if (filters.status)
            filtered = filtered.filter(
                (i) => i.approve_status?.toLowerCase() === filters.status.toLowerCase()
            );

        setFilteredData(filtered);
    }, [filters, data]);

    /* ------------------- Editable Modal ------------------- */
    const handleOpenModal = (record) => {
        setSelectedRecord(record);
        form.setFieldsValue(record);
        setDirtyFields(new Set());
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setSelectedRecord(null);
        form.resetFields();
        setDirtyFields(new Set());
    };

    // Auto-calculate when any relevant field changes
    const onValuesChange = (changed, all) => {
        const changedKey = Object.keys(changed)[0];
        setDirtyFields((prev) => new Set(prev).add(changedKey));

        const calc = calculateSalary(all);
        form.setFieldsValue(calc);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const payload = {};

            // send only dirty fields + calculated fields
            dirtyFields.forEach((field) => {
                payload[field] = values[field];
            });
            const calc = calculateSalary(values);
            Object.assign(payload, calc);

            const res = await axios.post(
                `${backUrl}/api/payroll/generate-salary/${selectedRecord.id}/?month=${selectedRecord.month}&year=${selectedRecord.year}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success("Salary details updated successfully!");
            handleCloseModal();
            fetchData();
        } catch (error) {
            console.error("Error updating salary:", error);
            toast.error(
                error.response?.data?.detail || "Failed to update salary details"
            );
        }
    };

    /* ------------------- Filter Handlers ------------------- */
    const handleOrgChange = (orgId) => {
        const org = organizationNames.find(o => o.id === orgId);
        setFilters({
            ...filters,
            organization: org?.name || "",
            department: "",
            designation: "",
        });
        setDepartmentNames([]);
        setDesignationNames([]);
        if (orgId) fetchDepartments(orgId);
    };

    const handleDeptChange = (deptId) => {
        const dept = departmentNames.find(d => d.id === deptId);
        setFilters({
            ...filters,
            department: dept?.name || "",
            designation: "",
        });
        setDesignationNames([]);
        if (deptId) fetchDesignations(deptId);
    };

    const handleDesignationChange = (desId) => {
        const des = designationNames.find(d => d.id === desId);
        setFilters({
            ...filters,
            designation: des?.name || "",
        });
    };

    /* ------------------- Table Columns ------------------- */
    const columns = [
        {
            title: "Emp Code",
            dataIndex: ["employee", "emp_code"],
            key: "emp_code",
            fixed: "left",
            render: (text, record) => (
                <a
                    style={{ color: "#1677ff", fontWeight: 500, cursor: "pointer" }}
                    onClick={() => {
                        setSelectedSalary({
                            ...selectedSalary,
                            id: record.id,
                            basic: record.basic_earned,
                            hra: record.hra_earned,
                            conveyance: record.conveyance_earned,
                            others: record.other_allowance_earned,
                            pf: record.pf_employee,
                            esi: record.esi_employee,
                            gross: record.gross_earned,
                            total_deduction: record.total_deductions,
                            net: record.net_pay,
                        });
                        handleOpenModal(record);
                    }}
                >
                    {text}
                </a>
            ),
        },
        {
            title: "Name",
            key: "employee_name",
            render: (record) =>
                `${record.employee?.first_name || ""} ${record.employee?.last_name || ""}`,
        },
        { title: "Organisation", dataIndex: ["employee", "organisation_name"], key: "organisation_name", width: 200 },
        { title: "Department", dataIndex: ["employee", "department_name"], key: "department_name", width: 200 },
        { title: "Designation", dataIndex: ["employee", "designation_name"], key: "designation_name", width: 200 },
        { title: "Month Days", dataIndex: "month_days", key: "month_days" },
        { title: "Days Payable", dataIndex: "days_payable", key: "days_payable" },
        { title: "Basic", dataIndex: "basic_earned", key: "basic_earned" },
        { title: "Gross", dataIndex: "gross_earned", key: "gross_earned" },
        { title: "HRA", dataIndex: "hra_earned", key: "hra_earned" },
        { title: "Conveyance", dataIndex: "conveyance_earned", key: "conveyance_earned" },
        { title: "Other Allowances", dataIndex: "other_allowance_earned", key: "other_allowance_earned" },
        { title: "OT Days", dataIndex: "no_of_ot_days", key: "no_of_ot_days" },
        { title: "OT Hours", dataIndex: "no_of_ot_hours", key: "no_of_ot_hours" },
        { title: "OT Pay", dataIndex: "ot_pay", key: "hourly_ot_amount" },
        { title: "PT", dataIndex: "pt_pm", key: "pt" },
        { title: "Employer PF (12%)", dataIndex: "pf_employer_pm", key: "pf_employer" },
        { title: "Employer ESI (3.25%)", dataIndex: "esi_employer_pm", key: "esi_employer" },
        { title: "Employee PF", dataIndex: "pf_employee_pm", key: "pf_employee" },
        { title: "Employee ESI", dataIndex: "esi_employee_pm", key: "esi_employee" },
        { title: "Flexible Benefit Allowance", dataIndex: "flexible_benefit_allowance", key: "flexible_benefit_allowance" },
        { title: "Late days", dataIndex: "late_days", key: "late_days" },
        { title: "Late deduction", dataIndex: "late_deduction", key: "late_deduction" },
        { title: "LOP days", dataIndex: "lop_days", key: "lop_days" },
        { title: "LOP deduction", dataIndex: "lop_deduction", key: "lop_deduction" },
        { title: "Salary Advance", dataIndex: "salary_advance", key: "salary_advance" },
        { title: "Arrears", dataIndex: "arrears", key: "arrears" },
        { title: "Total CTC", dataIndex: "ctc_earned", key: "ctc_monthly" },
        { title: "Net Pay", dataIndex: "net_pay_earned", key: "net_earned" },
        { title: "Net Pay in Words", dataIndex: "net_pay_in_words", key: "net_earned" },
        { title: "Salary type", dataIndex: "salary_type", key: "salary_type" },
        { title: "Salary status", dataIndex: "salary_status", key: "salary_status" },
        { title: "CL OP", dataIndex: "cl_opening", key: "cl_opening" },
        { title: "SL OP", dataIndex: "sl_opening", key: "sl_opening" },
        { title: "EL OP", dataIndex: "el_opening", key: "el_opening" },
        { title: "CL Availed", dataIndex: "cl_availed", key: "cl_availed" },
        { title: "SL Availed", dataIndex: "sl_availed", key: "sl_availed" },
        { title: "EL Availed", dataIndex: "el_availed", key: "el_availed" },
        { title: "CL Balance", dataIndex: "cl_balance", key: "cl_balance" },
        { title: "SL Balance", dataIndex: "sl_balance", key: "sl_balance" },
        { title: "EL Balance", dataIndex: "el_balance", key: "el_balance" },
        { title: "Date", dataIndex: "date", key: "date" },
        { title: "Month", dataIndex: "salary_month", key: "salary_month" },
        { title: "Year", dataIndex: "year", key: "year" },
        { title: "Approval Status", dataIndex: "approve_status", key: "approve_status" },
    ];

    const handleBulkApprove = async () => {
        if (!selectedRowKeys.length) {
            toast.warning("Please select at least one record to approve!");
            return;
        }

        try {
            const res = await axios.post(
                `${backUrl}/api/payroll/bulk_submit_salary/`,
                { ids: selectedRowKeys },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success(res.data.message || "Selected salaries approved successfully!");
            setSelectedRowKeys([]);
            fetchData();
        } catch (error) {
            console.error("Bulk approve error:", error);
            toast.error(error.response?.data?.detail || "Failed to approve salaries");
        }
    };

    /* ------------------- Render ------------------- */
    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />

            <center>
                <h5 style={{ marginTop: 20 }}>Actual Salary Statement</h5>
            </center>

            {/* Filters */}
            <Card style={{ margin: 20 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Organization"
                            style={{ width: "100%" }}
                            value={organizationNames.find(o => o.name === filters.organization)?.id}
                            onChange={handleOrgChange}
                            allowClear
                        >
                            {organizationNames.map((org) => (
                                <Select.Option key={org.id} value={org.id}>
                                    {org.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>

                    <Col>
                        <Select
                            placeholder="Department"
                            style={{ width: "100%" }}
                            value={departmentNames.find(d => d.name === filters.department)?.id}
                            onChange={handleDeptChange}
                            allowClear
                            disabled={!filters.organization}
                        >
                            {departmentNames.map((dep) => (
                                <Select.Option key={dep.id} value={dep.id}>
                                    {dep.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>

                    <Col>
                        <Select
                            placeholder="Designation"
                            style={{ width: "100%" }}
                            value={designationNames.find(d => d.name === filters.designation)?.id}
                            onChange={handleDesignationChange}
                            allowClear
                            disabled={!filters.department}
                        >
                            {designationNames.map((des) => (
                                <Select.Option key={des.id} value={des.id}>
                                    {des.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input
                            placeholder="Emp Code"
                            value={filters.emp_code}
                            onChange={(e) =>
                                setFilters({ ...filters, emp_code: e.target.value })
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input
                            placeholder="Name"
                            value={filters.name}
                            onChange={(e) =>
                                setFilters({ ...filters, name: e.target.value })
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Input
                            placeholder="Year"
                            type="number"
                            value={filters.year}
                            onChange={(e) =>
                                setFilters({ ...filters, year: e.target.value })
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Select
                            placeholder="Month"
                            style={{ width: "100%" }}
                            value={filters.month || undefined}
                            onChange={(v) => setFilters({ ...filters, month: v })}
                            allowClear
                        >
                            {Array.from({ length: 12 }, (_, i) => (
                                <Select.Option key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString("default", {
                                        month: "long",
                                    })}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Select
                            placeholder="Status"
                            style={{ width: "100%" }}
                            value={filters.status || undefined}
                            onChange={(v) => setFilters({ ...filters, status: v })}
                            allowClear
                        >
                            <Select.Option value="Pending">Pending</Select.Option>
                            <Select.Option value="Approved">Approved</Select.Option>
                            <Select.Option value="Rejected">Rejected</Select.Option>
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* Action Buttons + Table */}
            <Card style={{ margin: 20 }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "10px",
                        marginBottom: 10,
                    }}
                >
                    {selectedRowKeys.length > 0 && (
                        <Button
                            type="primary"
                            style={{
                                backgroundColor: "green",
                                borderColor: "green",
                                fontWeight: "500",
                                color: 'white'
                            }}
                            onClick={handleBulkApprove}
                            disabled={!selectedRowKeys.length}
                        >
                            Request Approval
                        </Button>
                    )}
                    <Button
                        icon={<DownloadOutlined />}
                        style={{
                            backgroundColor: "#0e497b",
                            borderColor: "#0e497b",
                            color: "white",
                            fontWeight: "500",
                        }}
                        onClick={handleDownloadExcel}
                    >
                        Download Excel
                    </Button>

                    <Button
                        icon={<UploadOutlined />}
                        style={{
                            backgroundColor: "#0e497b",
                            borderColor: "#0e497b",
                            color: "white",
                            fontWeight: "500",
                        }}
                        onClick={handleOpenUploadModal}
                    >
                        Upload Salary Details
                    </Button>

                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{
                            backgroundColor: "#0e497b",
                            borderColor: "#0e497b",
                            fontWeight: "500",
                        }}
                        onClick={handleOpenGenerateModal}
                    >
                        Generate Salary
                    </Button>
                </div>

                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    bordered
                    scroll={{ x: 7000 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                        textAlign: "center",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />

            </Card>

            {/* ---------- Generate Salary Modal ---------- */}
            <Modal
                open={isGenerateModalVisible}
                onCancel={handleCloseGenerateModal}
                title="Generate Salary"
                footer={null}
                width={500}
            >
                <Form layout="vertical" form={generateForm}>
                    <Form.Item
                        label="Select Month and Year"
                        name="month_year"
                        rules={[{ required: true, message: "Please select month and year!" }]}
                    >
                        <DatePicker
                            picker="month"
                            style={{ width: "100%" }}
                            format="MMMM YYYY"
                            placeholder="Select Month and Year"
                        />
                    </Form.Item>

                    <div style={{ textAlign: "right" }}>
                        <Button
                            onClick={handleCloseGenerateModal}
                            style={{ marginRight: 10 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            loading={generating}
                            onClick={handleGenerateSalary}
                            style={{
                                backgroundColor: "#0e497b",
                                borderColor: "#0e497b",
                                fontWeight: "500",
                            }}
                        >
                            Generate
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* ---------- Upload Modal ---------- */}
            <Modal
                open={isUploadModalVisible}
                onCancel={handleCloseUploadModal}
                title="Upload Salary Details"
                footer={null}
                width={500}
            >
                <Form layout="vertical" form={uploadForm}>
                    <Form.Item
                        label="Select Month and Year"
                        name="month_year"
                        rules={[{ required: true, message: "Please select month and year!" }]}
                    >
                        <DatePicker
                            picker="month"
                            style={{ width: "100%" }}
                            format="MMMM YYYY"
                            placeholder="Select Month and Year"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Upload File"
                        name="file"
                        valuePropName="fileList"
                        getValueFromEvent={(e) =>
                            Array.isArray(e) ? e : e && e.fileList
                        }
                        rules={[{ required: true, message: "Please upload a file!" }]}
                    >
                        <Upload beforeUpload={() => false} accept=".xlsx" maxCount={1}>
                            <Button icon={<UploadOutlined />}>Choose File</Button>
                        </Upload>
                    </Form.Item>

                    <div style={{ textAlign: "right" }}>
                        <Button
                            onClick={handleCloseUploadModal}
                            style={{ marginRight: 10 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            loading={uploading}
                            onClick={handleUploadSalary}
                            style={{
                                backgroundColor: "#0e497b",
                                borderColor: "#0e497b",
                                fontWeight: "500",
                            }}
                        >
                            Upload
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* ---------- Edit Modal ---------- */}
            <Modal
                open={isModalVisible}
                onCancel={handleCloseModal}
                footer={null}
                title={`Edit Salary – ${selectedRecord?.employee?.emp_code || ""} – ${selectedRecord?.employee?.first_name || ""
                    }`}
                width={900}
            >
                <SalaryCalculation salary_details={selectedSalary} />
            </Modal>
        </>
    );
}